import type { CompTopicExampleRow } from "@nexus/contracts";
import { Button } from "@nexus/ui";
import { SlidersHorizontal } from "lucide-react";
import { useTranslation } from "react-i18next";
import { formatTopicTitle } from "../OverviewChart/chartPrimitives.js";
import type { CompTopicDetailContext } from "../../lib/compTopicDetailContext.js";
import { buildCompTopicDetailPerformanceCards } from "../../lib/compTopicDetailMetrics.js";
import { CompTopicDetailPreviewPanel } from "./CompTopicDetailPreviewPanel.js";

interface CompTopicDetailInfoTabProps {
  context: CompTopicDetailContext;
  examples: CompTopicExampleRow[];
  examplesLoading: boolean;
  selectedExampleIndex: number;
  onSelectedExampleIndexChange: (index: number) => void;
}

export function CompTopicDetailInfoTab({
  context,
  examples,
  examplesLoading,
  selectedExampleIndex,
  onSelectedExampleIndexChange,
}: CompTopicDetailInfoTabProps) {
  const { t } = useTranslation();
  const performanceCards = buildCompTopicDetailPerformanceCards(context, examples);

  return (
    <div className="grid min-h-0 flex-1 grid-cols-1 gap-6 px-6 py-5 lg:grid-cols-2">
      <div className="flex flex-col gap-6">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            {t("overview.compTopicDetail.topicLabel", { defaultValue: "Topic" })}
          </p>
          <h3 className="mt-1 text-base font-medium leading-snug text-foreground">
            {formatTopicTitle(context.topicTitle)}
          </h3>
          {context.topicDescription && (
            <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">
              {context.topicDescription}
            </p>
          )}
        </div>

        <dl className="grid gap-3 text-sm">
          <div className="grid grid-cols-[120px_1fr] gap-2">
            <dt className="text-muted-foreground">
              {t("overview.compTopicDetail.competitorLabel", { defaultValue: "Competitor" })}
            </dt>
            <dd className="font-medium text-foreground">{context.entityDisplayLabel}</dd>
          </div>
          <div className="grid grid-cols-[120px_1fr] gap-2">
            <dt className="text-muted-foreground">
              {t("overview.compTopicDetail.timeFramesLabel", { defaultValue: "Time Frames" })}
            </dt>
            <dd className="font-medium text-foreground">{context.featureTimeFrame}</dd>
          </div>
        </dl>

        <div>
          <div className="mb-4 flex items-center justify-between gap-3">
            <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              {t("overview.compTopicDetail.performance", { defaultValue: "Performance" })}
            </p>
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled
              className="h-7 gap-1.5 text-xs"
              aria-label={t("overview.topicDetail.customizeMetrics", { defaultValue: "Customize metrics" })}
            >
              <SlidersHorizontal className="h-3.5 w-3.5" aria-hidden />
              {t("overview.topicDetail.metricsButton", { defaultValue: "Metrics" })}
            </Button>
          </div>

          <div
            className="grid grid-cols-2 gap-3 sm:grid-cols-3"
            data-testid="comp-topic-detail-performance-cards"
          >
            {performanceCards.map(({ id, labelKey, value }) => (
              <div
                key={id}
                className="min-w-0 rounded-lg border border-border bg-muted/30 px-4 py-3"
              >
                <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                  {t(labelKey)}
                </p>
                <p className="mt-1 truncate text-lg font-medium tabular-nums text-foreground">{value}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      <CompTopicDetailPreviewPanel
        rows={examples}
        selectedIndex={selectedExampleIndex}
        onSelectedIndexChange={onSelectedExampleIndexChange}
        isLoading={examplesLoading}
      />
    </div>
  );
}
